import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/auth-context";
import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoute } from "@/routes/protected-route";
import { SectionGuard } from "@/routes/section-guard";
import LoginPage from "@/routes/login";
import ChangePasswordPage from "@/routes/change-password";
import DashboardLayout from "@/routes/dashboard/layout";
import DashboardHome from "@/routes/dashboard";
import AdminSettingsPage from "@/routes/dashboard/admin-settings";
import { PlaceholderPage } from "@/routes/dashboard/placeholder";
import ILWebsiteIndex from "@/routes/dashboard/il-website";
import ArticlesList from "@/routes/dashboard/il-website/articles-list";
import ArticleEditor from "@/routes/dashboard/il-website/article-editor";
import TranslationsList from "@/routes/dashboard/translations";
import TranslationEditor from "@/routes/dashboard/translations/editor";
import AnalystReportsPage from "@/routes/dashboard/analyst-reports";
import NewsPortalIndex from "@/routes/dashboard/news-portal";
import CommentsPage from "@/routes/dashboard/comments";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: false },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Toaster />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<LoginPage />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<DashboardHome />} />

                <Route element={<SectionGuard section="adminSettings" />}>
                  <Route path="admin-settings" element={<AdminSettingsPage />} />
                </Route>

                <Route element={<SectionGuard section="ilWebsite" />}>
                  <Route path="il-website" element={<ILWebsiteIndex />} />
                  <Route path="il-website/articles-editor" element={<ArticlesList />} />
                  <Route
                    path="il-website/articles-editor/:id"
                    element={<ArticleEditor />}
                  />
                </Route>

                <Route element={<SectionGuard section="translations" />}>
                  <Route path="translations" element={<TranslationsList />} />
                  <Route path="translations/:ticker" element={<TranslationEditor />} />
                </Route>

                {/* Placeholders — sections kept in the sidebar but not yet ported. */}
                <Route element={<SectionGuard section="payments" />}>
                  <Route path="payments" element={<PlaceholderPage section="payments" />} />
                </Route>
                <Route element={<SectionGuard section="marketing" />}>
                  <Route path="marketing" element={<PlaceholderPage section="marketing" />} />
                </Route>
                <Route element={<SectionGuard section="userManagement" />}>
                  <Route path="users" element={<PlaceholderPage section="userManagement" />} />
                </Route>
                <Route element={<SectionGuard section="newswire" />}>
                  <Route path="newswire" element={<PlaceholderPage section="newswire" />} />
                </Route>
                <Route element={<SectionGuard section="newsPortal" />}>
                  <Route path="news-portal" element={<NewsPortalIndex />} />
                  <Route path="news-portal/comments" element={<CommentsPage />} />
                </Route>
                <Route element={<SectionGuard section="b2b" />}>
                  <Route path="b2b" element={<PlaceholderPage section="b2b" />} />
                </Route>
                <Route element={<SectionGuard section="portfolio" />}>
                  <Route path="portfolio" element={<PlaceholderPage section="portfolio" />} />
                </Route>
                <Route element={<SectionGuard section="alertsManager" />}>
                  <Route path="alerts" element={<PlaceholderPage section="alertsManager" />} />
                </Route>
                <Route element={<SectionGuard section="riskFactors" />}>
                  <Route path="risk-factors" element={<PlaceholderPage section="riskFactors" />} />
                </Route>
                <Route element={<SectionGuard section="analystReports" />}>
                  <Route path="analyst-reports" element={<AnalystReportsPage />} />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
